import { parseCardId, type CardId, type Seat, type Suit } from "@bing/engine-core";
import type { PassDirection, TrickPlay } from "./types";

export const QUEEN_OF_SPADES: CardId = "QS";
export const TWO_OF_CLUBS: CardId = "2C";

// There are only 52 distinct card ids, so memoize the parse — suit/rank are on
// the hot path (legality checks run many times per move).
const SUIT_CACHE = new Map<CardId, Suit>();
const RANK_CACHE = new Map<CardId, number>();

export function suitOf(id: CardId): Suit {
  let suit = SUIT_CACHE.get(id);
  if (suit === undefined) {
    suit = parseCardId(id).suit;
    SUIT_CACHE.set(id, suit);
  }
  return suit;
}

export function rankOf(id: CardId): number {
  let rank = RANK_CACHE.get(id);
  if (rank === undefined) {
    rank = parseCardId(id).rank;
    RANK_CACHE.set(id, rank);
  }
  return rank;
}

export function isHeart(id: CardId): boolean {
  return suitOf(id) === "hearts";
}

/** Point-carrying cards: every heart, plus the Queen of Spades. */
export function isPenalty(id: CardId): boolean {
  return isHeart(id) || id === QUEEN_OF_SPADES;
}

export function penaltyPoints(id: CardId): number {
  if (id === QUEEN_OF_SPADES) return 13;
  return isHeart(id) ? 1 : 0;
}

/** Hand `n` passes: 0→left, 1→right, 2→across, 3→hold (none), repeating. */
export function passDirectionFor(handNumber: number): PassDirection {
  return (["left", "right", "across", "none"] as const)[handNumber % 4]!;
}

/** Seat offset a giver's cards travel for a direction (clockwise seating). */
export function passOffset(dir: PassDirection): number {
  switch (dir) {
    case "left":
      return 1;
    case "across":
      return 2;
    case "right":
      return 3;
    case "none":
      return 0;
  }
}

/**
 * The cards `hand` may legally play, given the trick so far. Encodes: lead the
 * 2♣ on the first trick; follow suit if able; no hearts/Q♠ on the first trick
 * (unless forced); and no *leading* hearts until they are broken (unless the
 * hand is all hearts).
 */
export function legalPlays(
  hand: readonly CardId[],
  trick: readonly TrickPlay[],
  trickNumber: number,
  heartsBroken: boolean,
): CardId[] {
  const leading = trick.length === 0;
  const firstTrick = trickNumber === 0;

  if (leading) {
    if (firstTrick) {
      return hand.includes(TWO_OF_CLUBS) ? [TWO_OF_CLUBS] : [];
    }
    if (!heartsBroken) {
      const nonHearts = hand.filter((c) => !isHeart(c));
      if (nonHearts.length > 0) return nonHearts;
    }
    return [...hand];
  }

  const ledSuit = suitOf(trick[0]!.card);
  const inSuit = hand.filter((c) => suitOf(c) === ledSuit);
  let candidates = inSuit.length > 0 ? inSuit : [...hand];

  if (firstTrick) {
    const safe = candidates.filter((c) => !isPenalty(c));
    if (safe.length > 0) candidates = safe;
  }
  return candidates;
}

/** Winner of a completed (or partial) trick: highest card of the led suit. */
export function trickWinner(trick: readonly TrickPlay[]): Seat {
  let best = trick[0]!;
  const ledSuit = suitOf(best.card);
  for (const play of trick) {
    if (suitOf(play.card) === ledSuit && rankOf(play.card) > rankOf(best.card)) best = play;
  }
  return best.seat;
}

/**
 * Whether playing `card` breaks hearts — i.e. opens heart LEADS for everyone from here on.
 *
 * ⚠️ ONLY A HEART DOES. THE QUEEN OF SPADES DOES NOT, HOWEVER SHE IS DISCARDED — and this app
 * played the other way round until 2026-09-07. Both are real بی‌دل variants; heart-only is the
 * common one (eight Persian rule sources to one; English sources split the same way), so it is
 * what a player arriving here expects. Operator's decision — do not put the queen back without
 * one.
 *
 * This is the single place the rule lives: the engine's `legalMoves` is the only consumer of
 * `heartsBroken`, and the bots pick off that list (their rollouts run this very engine), so
 * nothing can hold a second opinion about what is legal to lead.
 */
export function breaksHearts(card: CardId): boolean {
  return isHeart(card);
}

export function handPenalties(taken: readonly (readonly CardId[])[]): number[] {
  return taken.map((cards) => cards.reduce((sum, c) => sum + penaltyPoints(c), 0));
}

/**
 * Score a completed hand. Normal: each seat scores its penalty points. Shooting
 * the moon (one seat took all 26): that seat scores 0, everyone else +26.
 */
export function scoreHand(taken: readonly (readonly CardId[])[]): {
  scores: number[];
  shooter: number | null;
} {
  const points = handPenalties(taken);
  const shooter = points.findIndex((p) => p === 26);
  if (shooter >= 0) {
    return { scores: points.map((_, i) => (i === shooter ? 0 : 26)), shooter };
  }
  return { scores: points, shooter: null };
}
